import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api';
import Footer from './Footer';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong'>('weak');

    useEffect(() => {
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate]);

    useEffect(() => {
        if (!newPassword) {
            setPasswordStrength('weak');
            return;
        }
        let strength = 0;
        if (newPassword.length >= 8) strength++;
        if (/[a-z]/.test(newPassword)) strength++;
        if (/[A-Z]/.test(newPassword)) strength++;
        if (/[0-9]/.test(newPassword)) strength++;
        if (/[^a-zA-Z0-9]/.test(newPassword)) strength++;

        if (strength <= 2) setPasswordStrength('weak');
        else if (strength <= 4) setPasswordStrength('medium');
        else setPasswordStrength('strong');
    }, [newPassword]);

    const getStrengthColor = () => {
        if (passwordStrength === 'strong') return 'bg-green-500';
        if (passwordStrength === 'medium') return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const getStrengthWidth = () => {
        if (passwordStrength === 'strong') return 'w-full';
        if (passwordStrength === 'medium') return 'w-2/3';
        return 'w-1/3';
    };

    const validate = (): string | null => {
        if (newPassword.length < 8) return 'Password must be at least 8 characters long';
        if (!/[A-Z]/.test(newPassword)) return 'Password must contain at least one uppercase letter';
        if (!/[a-z]/.test(newPassword)) return 'Password must contain at least one lowercase letter';
        if (!/[0-9]/.test(newPassword)) return 'Password must contain at least one number';
        if (!/[^a-zA-Z0-9]/.test(newPassword)) return 'Password must contain at least one special character';
        if (newPassword !== confirmPassword) return 'Passwords do not match';
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsLoading(true);
        try {
            await apiClient.resetPassword(token!, newPassword);
            toast.success('Password reset successfully. Please sign in.');
            navigate('/login');
        } catch (err: any) {
            setError(err.message || 'Failed to reset password. The link may have expired.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-white flex flex-col">
            <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-10" style={{ boxShadow: '0 20px 60px -12px rgba(0, 0, 0, 0.15), 0 8px 16px -8px rgba(0, 0, 0, 0.1)' }}>
                    <div className="text-center mb-8">
                        <img
                            src="/logo-horizontal-black.png"
                            alt="IntelliDrive"
                            className="mx-auto h-32 w-auto mb-3"
                        />
                        <p className="text-sm text-gray-600">Intelligent Driver Compliance Platform</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-1">Set a new password</h2>
                            <p className="text-sm text-gray-500">Choose a strong password for your account.</p>
                        </div>

                        {/* New Password */}
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                New password
                            </label>
                            <div className="relative">
                                <input
                                    id="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    required
                                    className="appearance-none block w-full px-4 py-3 pr-12 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.3)' } as React.CSSProperties}
                                    placeholder="Enter new password"
                                    value={newPassword}
                                    onChange={e => { setNewPassword(e.target.value); if (error) setError(null); }}
                                />
                                <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center" onClick={() => setShowNewPassword(!showNewPassword)}>
                                    {showNewPassword ? <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" /> : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />}
                                </button>
                            </div>
                            {newPassword && (
                                <div className="mt-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-gray-600">Password strength:</span>
                                        <span className={`text-xs font-medium ${passwordStrength === 'strong' ? 'text-green-600' : passwordStrength === 'medium' ? 'text-yellow-600' : 'text-red-600'}`}>
                                            {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div className={`h-1.5 rounded-full transition-all duration-300 ${getStrengthColor()} ${getStrengthWidth()}`}></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                Confirm new password
                            </label>
                            <div className="relative">
                                <input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    required
                                    className="appearance-none block w-full px-4 py-3 pr-12 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.3)' } as React.CSSProperties}
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChange={e => { setConfirmPassword(e.target.value); if (error) setError(null); }}
                                />
                                <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                    {showConfirmPassword ? <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" /> : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />}
                                </button>
                            </div>
                        </div>

                        {/* Requirements */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <h4 className="text-xs font-semibold text-blue-900 mb-2">Password requirements:</h4>
                            <ul className="text-xs text-blue-800 space-y-1">
                                {[
                                    [newPassword.length >= 8, 'At least 8 characters'],
                                    [/[A-Z]/.test(newPassword), 'At least one uppercase letter'],
                                    [/[a-z]/.test(newPassword), 'At least one lowercase letter'],
                                    [/[0-9]/.test(newPassword), 'At least one number'],
                                    [/[^a-zA-Z0-9]/.test(newPassword), 'At least one special character'],
                                ].map(([met, label]) => (
                                    <li key={label as string} className="flex items-center gap-1.5">
                                        <CheckCircle className={`w-3 h-3 flex-shrink-0 ${met ? 'text-green-600' : 'text-gray-300'}`} />
                                        {label as string}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {error && (
                            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                <span className="text-sm text-red-800">{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || !newPassword || !confirmPassword}
                            className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            style={{ backgroundColor: '#69BE28', '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                    Resetting...
                                </div>
                            ) : (
                                'Reset password'
                            )}
                        </button>

                        <Link
                            to="/login"
                            className="flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to sign in
                        </Link>
                    </form>
                </div>
            </div>
            <Footer />
        </div>
    );
}
